import { useNavigate } from "@/router";
import DetailsCard from "@/components/DetailsCard";
import IconButton from "@/components/IconButton";
import Menu from "@/components/Menu";
import SectionHeading from "@/components/SectionHeading";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { renderStatus } from "@/pages/billing/_utils";
import type { OrderPaymentStatus, OrderStage } from "@/pages/billing/types";
import type { ApplicationOrderSummary } from "../types";

interface ApplicationOrdersSectionProps {
  registrationOrder?: ApplicationOrderSummary | null;
  upcomingDuesOrder?: ApplicationOrderSummary | null;
}

interface OrderCardConfig {
  order: ApplicationOrderSummary;
  overline: string;
  testId: string;
}

function buildOrderCardOptions(
  order: ApplicationOrderSummary,
  overline: string,
  navigate: ReturnType<typeof useNavigate>
) {
  return [
    {
      iconName: "receipt_long" as const,
      overline,
      headline: order.description,
      supportingText: toDateTimeFormat(order.updatedAt) ?? undefined,
      action: () =>
        navigate("/billing/orders/:orderId", {
          params: { orderId: order.id }
        }),
      renderActions: () => (
        <div className="flex items-center -mt-3">
          <span className="text-label-small text-sys-brand-on-surface-variant">
            {centsToDollarsFormatted(order.balance)}
          </span>
          <Menu
            AnchorComponent={
              <IconButton name="more_vert" className="text-sys-brand-on-surface" iconClassName="font-extrabold" />
            }
            menuItems={[
              {
                label: "View Order",
                onClick: () =>
                  navigate("/billing/orders/:orderId", {
                    params: { orderId: order.id }
                  })
              }
            ]}
          />
        </div>
      ),
      status: () =>
        renderStatus(order.status as unknown as OrderStage, order.paymentStatus as unknown as OrderPaymentStatus)
    }
  ];
}

export default function ApplicationOrdersSection({
  registrationOrder,
  upcomingDuesOrder
}: ApplicationOrdersSectionProps) {
  const navigate = useNavigate();

  const orderCards: OrderCardConfig[] = [];

  if (registrationOrder) {
    orderCards.push({
      order: registrationOrder,
      overline: upcomingDuesOrder ? "Registration Order" : "Order",
      testId: "application-registration-order-card"
    });
  }

  if (upcomingDuesOrder) {
    orderCards.push({
      order: upcomingDuesOrder,
      overline: "Renewal Order",
      testId: "application-renewal-order-card"
    });
  }

  if (orderCards.length === 0) {
    return null;
  }

  const heading = orderCards.length > 1 ? "Application Orders" : "Application Order";

  return (
    <>
      <SectionHeading heading={heading} body="View orders created when this application was approved" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {orderCards.map(({ order, overline, testId }) => (
          <div key={order.id} data-testid={testId}>
            <DetailsCard className="py-4 pl-4 pr-0" options={buildOrderCardOptions(order, overline, navigate)} />
          </div>
        ))}
      </div>
    </>
  );
}
